import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, MenuItem, Alert, CircularProgress, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CropIcon from '@mui/icons-material/Crop';
import ReactCrop, { PercentCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import useIsMobile from '../hooks/useIsMobile';
import {
  PieceDossier, PreparationRecadrage, COTE_MIN_LISIBLE,
  preparerRecadrage, convertirPieceDossier, cadreInitial, versRect, versPourcentages, pixelsDuCadre, libelleProportions, messageErreur
} from '../services/normesPjServices';

// Outil « Recadrer » de la fenêtre Normes PJ : sur un aperçu de la pièce, l'admin ajuste le cadre proposé (cadre
// automatique, ou cadre ajusté à la main lors de la conversion précédente) aux proportions de la norme, puis la pièce
// est convertie avec ce cadre. L'original n'est jamais modifié.

interface Props {
  chemin: string;
  piece: PieceDossier;
  titre: string;
  normeId: number | null;
  page: number | null; // PDF de plusieurs pages converti en image : page choisie dans la carte (null sinon)
  onClose: () => void;
  onConverti: (r: { piece: PieceDossier; copie: boolean }) => void;
}

const ARIA_CADRE = {
  cropArea: 'Zone gardée',
  nwDragHandle: 'Coin haut gauche de la zone',
  nDragHandle: 'Bord haut de la zone',
  neDragHandle: 'Coin haut droit de la zone',
  eDragHandle: 'Bord droit de la zone',
  seDragHandle: 'Coin bas droit de la zone',
  sDragHandle: 'Bord bas de la zone',
  swDragHandle: 'Coin bas gauche de la zone',
  wDragHandle: 'Bord gauche de la zone',
};

const enPourcentages = (r: { x: number; y: number; l: number; h: number }): PercentCrop => ({ unit: '%', ...versPourcentages(r) });

const RecadragePhotoDialog: React.FC<Props> = ({ chemin, piece, titre, normeId, page: pageCarte, onClose, onConverti }) => {
  const isMobile = useIsMobile();
  const [page, setPage] = useState<number | null>(pageCarte);
  const [prep, setPrep] = useState<PreparationRecadrage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [essai, setEssai] = useState(0);
  const [cadre, setCadre] = useState<PercentCrop | undefined>(undefined);
  const [envoi, setEnvoi] = useState(false);
  const [erreurConversion, setErreurConversion] = useState<string | null>(null);

  // Aperçu + cadre automatique de la page affichée (la réponse d'une page quittée entre-temps est ignorée)
  useEffect(() => {
    let actif = true;
    setPrep(null);
    setErreur(null);
    setErreurConversion(null);
    preparerRecadrage(chemin, piece.id, normeId, page)
      .then((p) => {
        if (!actif) return;
        setPrep(p);
        setCadre(enPourcentages(cadreInitial(p, piece.conversion, page)));
      })
      .catch((e) => {
        if (!actif) return;
        console.error('Erreur préparation du recadrage', e);
        setErreur(messageErreur(e, 'Aperçu impossible (backend injoignable ?)'));
      });
    return () => { actif = false; };
  }, [chemin, piece.id, piece.conversion, normeId, page, essai]);

  const regle = piece.regle;
  const sortie = regle ? regle.format_sortie : 'jpg';
  const pages = prep && prep.pages ? prep.pages : 0;
  const rect = cadre && cadre.width > 0 && cadre.height > 0 ? versRect(cadre) : null;
  const px = rect && prep ? pixelsDuCadre(rect, prep) : null;
  const proportions = libelleProportions(regle);

  // Avertissements du moteur de conversion, annoncés avant de convertir
  const minL = regle && regle.largeur_min ? regle.largeur_min : 0;
  const minH = regle && regle.hauteur_min ? regle.hauteur_min : 0;
  const minimum = minL && minL === minH ? `${minL} px`
    : [minL ? `largeur ${minL} px` : '', minH ? `hauteur ${minH} px` : ''].filter(Boolean).join(', ');
  const tropPetite = !!px && sortie !== 'pdf' && (px.l < minL || px.h < minH);
  const illisible = !!px && sortie === 'pdf' && Math.max(px.l, px.h) < COTE_MIN_LISIBLE;

  const valider = async () => {
    if (!rect) return;
    setEnvoi(true);
    setErreurConversion(null);
    try {
      onConverti(await convertirPieceDossier(chemin, { piece: piece.id, norme: normeId, page: pages > 1 ? page || 1 : null, recadrage: rect }));
    } catch (e) {
      console.error('Erreur conversion recadrée', e);
      setErreurConversion(messageErreur(e, 'Conversion impossible'));
      setEnvoi(false);
    }
  };

  return (
    <Dialog open onClose={envoi ? undefined : onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6 }}>
        Recadrer : {titre}
        <IconButton onClick={onClose} disabled={envoi} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Fermer">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {proportions ? `Proportions imposées par la norme : ${proportions}. ` : 'Proportions libres. '}
          Déplacez la zone ou tirez ses bords et ses coins : seule la zone encadrée sera gardée.
        </Typography>
        {pages > 1 && (
          <TextField select size="small" label="Page" value={page || 1} onChange={(e) => setPage(Number(e.target.value))}
            disabled={envoi} sx={{ mb: 1.5, minWidth: 140 }}>
            {Array.from({ length: pages }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>Page {n} / {pages}</MenuItem>)}
          </TextField>
        )}
        {erreur && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => setEssai((n) => n + 1)}>Réessayer</Button>}>
            {erreur}
          </Alert>
        )}
        {!prep && !erreur && <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>}

        {prep && (
          <>
            <Box sx={{ textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 1, p: 1 }}>
              <ReactCrop
                crop={cadre}
                onChange={(_, pourcents) => setCadre(pourcents)}
                aspect={prep.ratio || undefined}
                keepSelection
                ruleOfThirds
                minWidth={16}
                minHeight={16}
                disabled={envoi}
                ariaLabels={ARIA_CADRE}
              >
                <img src={prep.apercu} alt={`Aperçu à recadrer : ${titre}`}
                  style={{ display: 'block', maxWidth: '100%', maxHeight: isMobile ? '55vh' : '60vh' }} />
              </ReactCrop>
            </Box>
            {px && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Zone gardée : {px.l}×{px.h} px de l'image d'origine ({prep.largeur}×{prep.hauteur} px).
              </Typography>
            )}
          </>
        )}
        {tropPetite && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Zone plus petite que le minimum de la norme ({minimum}) : l'image sera agrandie et perdra en netteté.
          </Alert>
        )}
        {illisible && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Zone de moins de {COTE_MIN_LISIBLE} px de côté : le document risque d'être illisible.
          </Alert>
        )}
        {erreurConversion && <Alert severity="error" sx={{ mt: 1 }}>{erreurConversion}</Alert>}
      </DialogContent>

      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={() => prep && setCadre(enPourcentages(prep.suggestion))} disabled={!prep || envoi} sx={{ mr: 'auto' }}>
          Cadre automatique
        </Button>
        <Button onClick={onClose} disabled={envoi}>Annuler</Button>
        <Button
          variant="contained"
          onClick={valider}
          disabled={!prep || !rect || envoi}
          startIcon={envoi ? <CircularProgress size={16} color="inherit" /> : <CropIcon />}
        >
          {envoi ? 'Conversion…' : `Recadrer et convertir en ${sortie.toUpperCase()}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecadragePhotoDialog;
